import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProvider } from '../../contexts/ProviderContext';
import ProviderTeamAccessList from '../ProviderTeamAccessList';
import GoogleSheetsReconnect from '../GoogleSheetsReconnect';
import ProviderLayout from './ProviderLayout';
import SheetStockUpdatesCard from './SheetStockUpdatesCard';
import SupplierProfileForm from './SupplierProfileForm';
import { supabase } from '../../lib/supabase';
import {
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const ProviderSettings: React.FC = () => {
  const { currentProviderId } = useProvider();
  const [searchParams] = useSearchParams();
  const queryProviderId = searchParams.get('providerId');

  // Use query param providerId if present, otherwise use context providerId
  const effectiveProviderId = queryProviderId || currentProviderId;

  const [providerData, setProviderData] = useState<any>(null);
  const [providerConfig, setProviderConfig] = useState<any>(null);
  const [, setLoading] = useState(true);

  // Auto-detect OAuth callback to mount GoogleSheetsReconnect component
  const oauthCode = searchParams.get('code');
  const oauthState = searchParams.get('state');
  const isReturningFromOAuth = !!(oauthCode && oauthState && typeof window !== 'undefined' && sessionStorage.getItem('reconnect_provider_id'));

  const [showReconnect, setShowReconnect] = useState(isReturningFromOAuth);

  // Fetch provider and config data
  useEffect(() => {
    const fetchProviderData = async () => {
      if (!effectiveProviderId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch provider details
        const { data: provider, error: providerError } = await supabase
          .from('providers')
          .select('id, name, provider_type')
          .eq('id', effectiveProviderId)
          .single();

        if (providerError) throw providerError;
        setProviderData(provider);

        // Fetch provider config (includes requires_reauth and sync_errors)
        const { data: config, error: configError } = await supabase
          .from('provider_config')
          .select('*')
          .eq('provider_id', effectiveProviderId)
          .single();

        if (configError && configError.code !== 'PGRST116') {
          // PGRST116 is "not found", which is okay
          console.error('Error fetching provider config:', configError);
        }

        setProviderConfig(config);
      } catch (error) {
        console.error('Error fetching provider data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProviderData();
  }, [effectiveProviderId]);

  const handleReconnectSuccess = async () => {
    setShowReconnect(false);

    // Refresh provider config to clear the re-auth flag
    if (effectiveProviderId) {
      const { data: config } = await supabase
        .from('provider_config')
        .select('*')
        .eq('provider_id', effectiveProviderId)
        .single();

      setProviderConfig(config);
    }
  };

  const isGoogleSheetsProvider = providerData?.provider_type === 'google_sheets';
  const requiresReauth = providerConfig?.requires_reauth === true;
  const hasSyncErrors = providerConfig?.sync_errors && Array.isArray(providerConfig.sync_errors) && providerConfig.sync_errors.length > 0;
  const lastSyncAt = providerConfig?.last_sync_at;

  return (
    <ProviderLayout>
      <div className="max-w-4xl mx-auto">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white ">Provider Settings</h1>
          <p className="text-white/50  text-sm mt-1">Manage your provider account settings</p>
        </div>

        {!effectiveProviderId && (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 rounded">
            <p className="text-yellow-700  text-sm">No provider selected</p>
          </div>
        )}

        {effectiveProviderId && (
          <div className="space-y-6">
            {/* Google Sheets Re-authentication Warning */}
            {isGoogleSheetsProvider && requiresReauth && !showReconnect && (
              <div className="bg-red-500/10 border-2 border-red-500/20 p-6 rounded-xl">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="ml-3 flex-1">
                    <h3 className="text-base font-semibold text-red-400">
                      Authentication Required
                    </h3>
                    <p className="mt-2 text-sm text-red-400/70">
                      Your Google Sheets authorization has expired or been revoked. Product syncing is currently paused.
                      Please re-authenticate to resume automatic updates.
                    </p>
                    <div className="mt-4">
                      <button
                        onClick={() => setShowReconnect(true)}
                        className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-xl text-white bg-red-500 hover:bg-red-400 transition-colors"
                      >
                        Re-authenticate Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Google Sheets Reconnection Component */}
            {isGoogleSheetsProvider && showReconnect && providerConfig && (
              <GoogleSheetsReconnect
                providerId={effectiveProviderId}
                providerName={providerData?.name}
                spreadsheetId={providerConfig.spreadsheet_id}
                sheetName={providerConfig.sheet_name}
                onSuccess={handleReconnectSuccess}
                onCancel={() => setShowReconnect(false)}
              />
            )}

            {/* Sync Status Section for Google Sheets */}
            {isGoogleSheetsProvider && !requiresReauth && (
              <div className="glass-card rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white  mb-4">Sync Status</h3>

                <div className="space-y-3">
                  {/* Last Sync */}
                  {lastSyncAt && (
                    <div className="flex items-center text-sm">
                      <ClockIcon className="h-5 w-5 text-white/25 mr-2" />
                      <span className="text-white/50 ">Last synced:</span>
                      <span className="ml-2 text-white ">
                        {new Date(lastSyncAt).toLocaleString()}
                      </span>
                    </div>
                  )}

                  {/* Spreadsheet Info */}
                  {providerConfig?.spreadsheet_id && (
                    <div className="text-sm">
                      <span className="text-white/50 ">Spreadsheet ID:</span>
                      <code className="ml-2 bg-transparent px-2 py-1 rounded text-xs">
                        {providerConfig.spreadsheet_id}
                      </code>
                    </div>
                  )}

                  {/* Sync Errors */}
                  {hasSyncErrors && (
                    <div className="mt-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl rounded p-4">
                      <h4 className="text-sm font-semibold text-yellow-900  mb-2">
                        Recent Sync Issues
                      </h4>
                      <div className="space-y-2">
                        {providerConfig.sync_errors.slice(-3).reverse().map((err: any, idx: number) => (
                          <div key={idx} className="text-xs text-yellow-400 ">
                            {typeof err === 'object' ? (
                              <>
                                <div className="font-semibold">{err.timestamp ? new Date(err.timestamp).toLocaleString() : 'Unknown time'}</div>
                                <div className="ml-4 mt-1">{err.error || JSON.stringify(err)}</div>
                              </>
                            ) : (
                              <div>{String(err)}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Success Message */}
                  {!hasSyncErrors && lastSyncAt && (
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl rounded p-3">
                      <p className="text-sm text-emerald-400 ">
                        ✓ Syncing normally - no errors detected
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Automatic stock write-back (spreadsheet-stock-decrement) */}
            {isGoogleSheetsProvider && (
              <SheetStockUpdatesCard
                spreadsheetId={providerConfig?.spreadsheet_id}
                enabled={providerConfig?.config_object?.sheet_decrement_enabled === true}
              />
            )}

            {/* Terms merchants read before connecting */}
            <SupplierProfileForm providerId={effectiveProviderId} />

            {/* Team Access Section */}
            <div className="glass-card rounded-2xl p-6">
              <ProviderTeamAccessList providerId={effectiveProviderId} />
            </div>
          </div>
        )}
      </div>
    </ProviderLayout>
  );
};

export default ProviderSettings;
